import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './ui/motion.css';
import './ui/surfaces.css';
import { postAuthDestination } from './utils/redirect';
import Landing from './landing/LandingPage';
import Login from './pages/Login';
import StudentSignup from './pages/StudentSignup';
import RecruiterSignup from './pages/RecruiterSignup';
import StudentDashboard from './pages/StudentDashboard';
import RecruiterDashboard from './pages/RecruiterDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import StudentProfile from './pages/StudentProfile';
import Messaging from './pages/Messaging';
import PostJob from './pages/PostJob';
import RecruiterProfile from './pages/RecruiterProfile';
import CompleteStudentProfile from './pages/CompleteStudentProfile';
import CompleteRecruiterProfile from './pages/CompleteRecruiterProfile';
import JobDetail from './pages/JobDetail';
import ResumeScorePage from './pages/ResumeScorePage';
import FindStudents from './pages/FindStudents';
import Pricing from './pages/Pricing';
import RecruiterBilling from './pages/RecruiterBilling';

// Protected Route Component
// Exported so the recruiter-onboarding regression test can assert on the real
// guard rather than a copy of its logic.
export function ProtectedRoute({ children, requiredRole }) {
  const userData = localStorage.getItem('user');
  if (!userData) return <Navigate to="/" />;

  const user = JSON.parse(userData);
  if (user.role !== requiredRole) return <Navigate to="/" />;

  return children;
}

// Synchronous redirect — runs before any child renders, so no flash.
// Exported for the same reason as ProtectedRoute: the regression tests assert
// on the real guard rather than on a copy of its logic.
export function RequireNoAuth({ children }) {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const userData = localStorage.getItem('user');

  if (token && userData) {
    try {
      const user = JSON.parse(userData);
      // Someone who is already signed in and lands on /login?next=/jobs/<id>
      // — a shared link opened in a tab that still holds a session — wants the
      // job, not the dashboard. postAuthDestination() rejects anything that
      // would leave the app and falls back to the role's dashboard.
      const next = new URLSearchParams(location.search).get('next');
      const dest = postAuthDestination(user.role, next);
      if (dest !== '/') return <Navigate to={dest} replace />;
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  }

  return children;
}

function AppRoutes() {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  return (
    <Routes>
      <Route path="/" element={<RequireNoAuth><Landing /></RequireNoAuth>} />
      <Route path="/login" element={<RequireNoAuth><Login /></RequireNoAuth>} />
      <Route path="/student/signup" element={<RequireNoAuth><StudentSignup /></RequireNoAuth>} />
      <Route path="/recruiter/signup" element={<RequireNoAuth><RecruiterSignup /></RequireNoAuth>} />
      <Route path="/student/complete-profile" element={<CompleteStudentProfile />} />
      <Route path="/recruiter/complete-profile" element={<CompleteRecruiterProfile />} />

      <Route path="/student/dashboard" element={
        <ProtectedRoute requiredRole="student">
          <StudentDashboard onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/recruiter/dashboard" element={
        <ProtectedRoute requiredRole="recruiter">
          <RecruiterDashboard onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/student/resume-score" element={
        <ProtectedRoute requiredRole="student">
          <ResumeScorePage />
        </ProtectedRoute>
      } />

      <Route path="/recruiter/find-students" element={
        <ProtectedRoute requiredRole="recruiter">
          <FindStudents onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/recruiter/post-job" element={
        <ProtectedRoute requiredRole="recruiter">
          <PostJob onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/recruiter/post-job/:id" element={
        <ProtectedRoute requiredRole="recruiter">
          <PostJob onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Pricing is readable signed out so it can be linked from anywhere.
          Buying still requires a recruiter session — the page redirects to
          sign-in rather than opening a checkout that would 401 mid-flow. */}
      <Route path="/pricing" element={<Pricing onLogout={handleLogout} />} />

      <Route path="/recruiter/billing" element={
        <ProtectedRoute requiredRole="recruiter">
          <RecruiterBilling onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/recruiter/profile" element={
        <ProtectedRoute requiredRole="recruiter">
          <RecruiterProfile onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/admin/dashboard" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/hyr-admin" element={<AdminLogin />} />
      <Route path="/jobs/:id" element={<JobDetail />} />
      <Route path="/student/:id" element={<StudentProfile />} />

      <Route path="/messages" element={
        (() => {
          const userData = localStorage.getItem('user');
          if (!userData) return <Navigate to="/" />;
          const u = JSON.parse(userData);
          if (!['student', 'recruiter'].includes(u.role)) return <Navigate to="/" />;
          return <Messaging user={u} />;
        })()
      } />
    </Routes>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppRoutes />
    </Router>
  );
}

export default App;
